import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Lock, Mail, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import LanguageSelector from '../components/LanguageSelector';

const LOGO_URL = process.env.REACT_APP_LOGO_URL || 'https://customer-assets.emergentagent.com/job_sgficapital/artifacts/i5d6sap9_LOGO%20SGFI-nuevo.png';
const HERO_IMAGE = process.env.REACT_APP_HERO_IMAGE || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&q=80';
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Default brand colors
const DEFAULT_COLORS = {
  accentColor: '#2e8036',
  primaryColor: '#93a5af',
  heroBackground: '#0d1218',
  sectionBackground: '#1c262d'
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [colorsLoaded, setColorsLoaded] = useState(false);
  const [colors, setColors] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Fetch global colors before rendering
  useEffect(() => {
    const fetchColors = async () => {
      try {
        const response = await axios.get(`${API_URL}/content/landing`);
        if (response.data?.content?.styles) {
          setColors({ ...DEFAULT_COLORS, ...response.data.content.styles });
        } else {
          setColors(DEFAULT_COLORS);
        }
      } catch (err) {
        setColors(DEFAULT_COLORS);
      } finally {
        setColorsLoaded(true);
      }
    };
    fetchColors();
  }, []);

  // Show loading until colors are loaded
  if (!colorsLoaded || !colors) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0d1218' }}>
        <Loader2 className="w-8 h-8 animate-spin text-white/50" />
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      if (user.is_admin) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);

    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email: forgotEmail });
      setForgotSuccess(true);
    } catch (err) {
      setForgotError(err.response?.data?.detail || t('errors.somethingWentWrong'));
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotModal = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setForgotSuccess(false);
    setForgotError('');
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Hero Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom right, ${colors.heroBackground}f0, ${colors.sectionBackground}cc, ${colors.accentColor}40)` }} />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <img src={LOGO_URL} alt="SGFI Capital" className="h-16 w-auto object-contain" />
          <div className="space-y-6">
            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight font-['Outfit']">
              Terminal exclusivo para<br />
              <span style={{ color: colors.accentColor }}>membros da comunidade SGFI</span>
            </h1>
          </div>
          <p className="text-sm" style={{ color: `${colors.primaryColor}99` }}>
            © {new Date().getFullYear()} SGFI Capital. {t('landing.allRightsReserved')}
          </p>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 relative" style={{ backgroundColor: colors.heroBackground }}>
        {/* Language Selector - Top Right */}
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        
        <div className="w-full max-w-md space-y-8">
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <img src={LOGO_URL} alt="SGFI Capital" className="h-14 w-auto object-contain" />
          </div>

          <div className="text-center space-y-2">
            <h2 className="text-3xl font-bold text-white font-['Outfit']">{t('auth.welcomeBack')}</h2>
            <p style={{ color: colors.primaryColor }}>{t('auth.accessAccount')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400" data-testid="login-error">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" style={{ color: colors.primaryColor }}>Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: `${colors.primaryColor}80` }} />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  className="pl-12 h-14 border-white/10 text-white rounded-xl"
                  style={{ backgroundColor: colors.sectionBackground }}
                  required
                  data-testid="login-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" style={{ color: colors.primaryColor }}>{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: `${colors.primaryColor}80` }} />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-12 pr-12 h-14 border-white/10 text-white rounded-xl"
                  style={{ backgroundColor: colors.sectionBackground }}
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: `${colors.primaryColor}80` }}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm transition-colors"
                style={{ color: colors.accentColor }}
              >
                {t('auth.forgotPassword')}
              </button>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 text-white rounded-xl font-semibold text-lg transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: colors.accentColor, boxShadow: `0 0 20px ${colors.accentColor}50` }}
              data-testid="login-submit"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('common.loading')}
                </div>
              ) : (
                t('auth.login')
              )}
            </Button>
          </form>

          <p className="text-center text-sm" style={{ color: `${colors.primaryColor}99` }}>
            {t('auth.accessAccount')}
          </p>
        </div>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="border border-white/10 rounded-2xl p-6 max-w-md w-full animate-fade-in" style={{ backgroundColor: colors.sectionBackground }}>
              <h3 className="text-xl font-semibold text-white mb-4">{t('auth.resetPassword')}</h3>
              
              {forgotSuccess ? (
                <div className="space-y-4">
                  <div className="p-4 bg-[#2e8036]/10 border border-[#2e8036]/20 rounded-xl flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2e8036] mt-0.5" />
                    <div>
                      <p className="text-[#2e8036] font-medium">{t('auth.emailSent')}</p>
                      <p className="text-[#93a5af] text-sm mt-1">
                        {t('auth.checkInbox')}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={closeForgotModal}
                    className="w-full bg-[#2e8036] hover:bg-[#247029] text-white rounded-xl h-12"
                  >
                    {t('common.back')}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className="text-[#93a5af] text-sm">
                    {t('auth.checkInbox')}
                  </p>
                  
                  {forgotError && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {forgotError}
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label className="text-[#93a5af]">{t('auth.email')}</Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#93a5af]/50" />
                      <Input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="seu@email.com"
                        required
                        className="pl-12 h-14 bg-[#0d1218] border-white/10 text-white placeholder:text-[#93a5af]/50 rounded-xl focus:border-[#2e8036]/50 focus:ring-[#2e8036]/20"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      onClick={closeForgotModal}
                      className="flex-1 bg-white/10 hover:bg-white/20 text-white rounded-xl h-12"
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      disabled={forgotLoading || !forgotEmail}
                      className="flex-1 bg-[#2e8036] hover:bg-[#247029] text-white rounded-xl h-12 disabled:opacity-50"
                    >
                      {forgotLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        t('common.submit')
                      )}
                    </Button>
                  </div>
                  
                  <p className="text-xs text-[#93a5af]/70 text-center pt-2">
                    Ou contacte o administrador em{' '}
                    <a href="mailto:info@sgfi.capital" className="text-[#2e8036] hover:text-[#3d9945]">
                      info@sgfi.capital
                    </a>
                  </p>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
